import { useMemo } from 'react';

import { GeneralLedgerAccountType, ProvisionType } from '~/common/enums';
import {
  type UseAutocompleteOptionsArgs,
  type UseAutocompleteOptionsRes,
} from './autocomplete.types';
import { getDefaultAccountTranslation } from '@/shared/utils/hoa/hoaUtils';
import { type HomeownerAssociationAccountOption } from '@/types/trpc/hoa';
import { trpc } from '@/config/trpc';
import useAutocompleteOptions from './useAutocompleteOptions';

const glaTypeMapping = {
  [ProvisionType.FixedWorkCapital]: GeneralLedgerAccountType.FixedWorkCapital,
  [ProvisionType.SpareCapital]: GeneralLedgerAccountType.ReserveCapital,
  [ProvisionType.WorkCapital]: GeneralLedgerAccountType.WorkCapital,
};

type Options<
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
> = {
  homeownerAssociationId: string | null | undefined;
  startsWith?: string[];
  provisionType?: ProvisionType;
  includeDivisionKeyDescription?: boolean;
  excludeIds?: string[];
} & UseAutocompleteOptionsArgs<HomeownerAssociationAccountOption[], TMultiple, TDisableClearable>;

export const useGetAccountAutocompleteOptions = ({
  homeownerAssociationId,
  startsWith = [],
  provisionType = undefined,
  includeDivisionKeyDescription = false,
}: {
  homeownerAssociationId: string | null | undefined;
  startsWith?: string[];
  provisionType?: ProvisionType;
  includeDivisionKeyDescription?: boolean;
}) => {
  const { data: options, isLoading } = trpc.hoa.account.autocomplete.useQuery(
    {
      homeownerAssociationId: homeownerAssociationId as string,
      includeDivisionKeyDescription,
      startsWith: startsWith || [],
    },
    { enabled: !!homeownerAssociationId },
  );

  const translatedOptions = useMemo(() => {
    if (!options) return [];

    const translateLabel = (option: HomeownerAssociationAccountOption) => {
      const { label } = option;
      const { defaultAccount, number } = option.data;

      return getDefaultAccountTranslation(label, number, defaultAccount);
    };

    return options
      .filter((o) => !provisionType || o.data.type === glaTypeMapping[provisionType])
      .map((option) => ({
        ...option,
        label: translateLabel(option),
      }));
  }, [provisionType, options]);

  return { data: translatedOptions, isLoading };
};
const useHoaAccountAutocomplete = <
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
>({
  homeownerAssociationId,
  selected,
  startsWith = [],
  provisionType,
  includeDivisionKeyDescription = false,
  excludeIds,
  ...args
}: Options<TMultiple, TDisableClearable>): UseAutocompleteOptionsRes<
  HomeownerAssociationAccountOption[],
  TMultiple
> => {
  const { data, isLoading } = useGetAccountAutocompleteOptions({
    homeownerAssociationId,
    includeDivisionKeyDescription,
    provisionType,
    startsWith,
  });

  const filteredData = useMemo(() => {
    if (!excludeIds?.length) return data;

    const selectedIds: unknown[] = [selected].flat();

    return data.filter(
      (option) => !excludeIds.includes(option.value) || selectedIds.includes(option.value),
    );
  }, [data, excludeIds, selected]);

  const autocompleteProps = useAutocompleteOptions({
    data: filteredData,
    isLoading,
    selected,
    ...args,
  });

  return {
    ...autocompleteProps,
    disabled: !homeownerAssociationId || autocompleteProps.disabled,
  };
};

export default useHoaAccountAutocomplete;
